import React, { useCallback, useEffect, useMemo } from "react"
import { delay, discardResult, promiseAny } from "../../app-core/_common/promise"
import { ModalProps } from "../AppModal/AppModal"
import { appendQueryParam, updateUrl } from "../../app-core/_common/routing"
import { CloseIntent, ModalRouter, ModalWindow, StackEntry } from "./types"

export type ModalStack = {
    openModal: (context: ModalProps) => Promise<void>
    closeModal: (intent: CloseIntent) => Promise<void>
    closeModalAfterSubmit: (intent: CloseIntent) => Promise<void>
    closeAllModals: (intent: CloseIntent) => Promise<void>
    lastModal: () => StackEntry | undefined
    dismissModal: () => void
    isOpen: (modalType: ModalProps["modalType"]) => boolean
    isClosing: (modalType: ModalProps["modalType"]) => boolean
    modals: ModalProps[]
    onHistoryPopState: () => void
}

export const useModalStack = (router: ModalRouter, window: ModalWindow): ModalStack => {
    const stackRef = React.useRef<StackEntry[]>([])
    const [forceRender, setForceRender] = React.useState(false)

    const setStack = (cb: (prevStack: StackEntry[]) => StackEntry[]) => {
        stackRef.current = cb(stackRef.current)
        setForceRender((prev) => !prev)
    }

    const openModal = async (modalProps: ModalProps) => {
        setStack((prev) => [...prev, { ...modalProps, state: "open" }])
        // console.log("OPEN MODAL", modalProps.modalType)
        if (!modalProps.skipBrowserHistory) {
            // NOTE: we add modal query param to be sure the browser creates a new history entry. Probably it would be better to create a random value
            await updateUrl(router, appendQueryParam(location.href, "modal", modalProps.modalType))
            // history.pushState(null, "", addQueryParam(location.href, "modal", modalProps.modalType))
        }

        return Promise.resolve()
    }

    const closeModalAfterSubmit = async (intent: CloseIntent): Promise<void> => {
        if (stackRef.current.length == 0) return Promise.resolve()
        const current = lastModal()

        dismissModal()
        if (intent === "closeWithCallback") current.onClose?.()
        return Promise.resolve()
    }

    const closeModal = async (intent: CloseIntent): Promise<void> => {
        // console.log("CLOSE MODAL", intent, stackRef.current)
        if (stackRef.current.length == 0) return Promise.resolve()
        const current = lastModal()

        current.closeIntent = intent

        if (current.skipBrowserHistory) {
            dismissModal()
            // TODO: duplicated inside onHistoryPopState. Should dismissModal do this?
            if (intent === "closeWithCallback") current.onClose?.()
            return Promise.resolve()
        } else {
            // console.log("CLOSE MODAL - ROUTER BACK")
            current.state = "closing"
            const closeAction = new Promise<void>((resolve) => {
                current.resolveClose = resolve
            })
            router.back()

            // NOTE: Available from node15 onward
            // return Promise.any([closeAction, discardResult(delay(50))])
            return promiseAny([closeAction, discardResult(delay(50))])
        }
    }

    const closeAllModals = async (intent: CloseIntent): Promise<void> => {
        await Promise.allSettled(stackRef.current.map(() => closeModal(intent)))
    }

    const dismissModal = useCallback(() => {
        setStack((prev) => [...prev].slice(0, prev.length - 1))
    }, [])

    const isOpen = (modalType: ModalProps["modalType"]) =>
        stackRef.current.some((s) => s.modalType === modalType && s.state === "open")

    const isClosing = (modalType: ModalProps["modalType"]) =>
        stackRef.current.some((s) => s.modalType === modalType && s.state === "closing")

    const lastModal = () => stackRef.current[stackRef.current.length - 1]

    const onHistoryPopState = useCallback(() => {
        // console.log("POP STATE", stackRef.current)

        if (stackRef.current.length == 0) return
        const current = lastModal()

        // if the modal is set to skip the browser history,
        // we exit the flow since the close is handled by closeModal()
        if (current.skipBrowserHistory) return

        // NOTE: It doesn't work in case we were trying to close the modal programmatically (closeModal).
        // What were we fixing here?
        // if (new URL(location.href).searchParams.getAll("modal").includes(current.modalType)) return

        // console.log("POP STATE - DISMISS MODAL", current.modalType)
        dismissModal()
        current.resolveClose?.()
        current.state = "closed"

        if (!current.closeIntent || current.closeIntent === "closeWithCallback") current.onClose?.()
    }, [dismissModal])

    useEffect(() => {
        router.beforePopState((state) => {
            // console.log("BEFORE POP STATE", state)
            state.options.scroll = false
            return true
        })

        window.addEventListener("popstate", onHistoryPopState)

        return () => {
            window.removeEventListener("popstate", onHistoryPopState)
        }
    }, [router, window, onHistoryPopState])

    return useMemo<ModalStack>(
        () => ({
            closeModal,
            closeModalAfterSubmit,
            closeAllModals,
            dismissModal,
            openModal,
            isOpen,
            isClosing,
            lastModal,
            modals: stackRef.current,
            onHistoryPopState,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [forceRender],
    )
}
